import React, { useState, useEffect } from "react";
import { useUser } from "../../providers/UserProvider";
import { PermissionEnum } from "../../../../shared/frontend/enums/PermissionEnum";
import {
  Stack,
  DefaultButton,
  Label,
  Checkbox,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";
import Download from "@mui/icons-material/Download";
import { HistoryButton } from "../../components/HistoryButton/HistoryButton";
import { useUseCaseContext } from "../../hooks/UseCaseContext";
import style from "./HeaderButtons.module.css";
import MetadataDropdown from "../MetadataDropdown";
import { DEFAULT_USE_CASE_NAME } from "../../utils/constants";
import DgEnvironmentSelector from "../DgEnvironmentSelector/DgEnvironmentSelector";

interface HeaderButtonsProps {
  showHistoryBtn: boolean;
  showHistoryPanel: boolean;
  onSetShowHistoryPanel: () => void;
  conversationId: string;
  onUseInternetChange: (checked: boolean) => void;
  onExport: (id: string, format: string) => Promise<void>;
  showExport: boolean;
  onFolderStructureChanged: (selected_folders: number) => void;
  useCaseTitle: string;
  currentDg: string;
  onDgSwitch: (dg: string, displayName?: string) => void;
}

const HeaderButtons: React.FC<HeaderButtonsProps> = ({
  showHistoryBtn,
  showHistoryPanel,
  onSetShowHistoryPanel,
  conversationId,
  onUseInternetChange,
  onExport,
  showExport,
  onFolderStructureChanged,
  useCaseTitle,
  currentDg,
  onDgSwitch,
}) => {
  const { useCase, setUseCase } = useUseCaseContext();
  const { hasPermission } = useUser();
  const canViewMultipleDg = hasPermission(PermissionEnum.VIEW_MULTIPLE_DG);
  const [useInternet, setUseInternet] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    setUseCase(useCase);
  }, [useCase, setUseCase]);

  useEffect(() => {
    onUseInternetChange(true);
  }, []);

  const handleInternetChange = (
    _ev?: React.FormEvent<HTMLElement | HTMLInputElement>,
    checked?: boolean,
  ) => {
    const newValue = !!checked;
    setUseInternet(newValue);
    onUseInternetChange(newValue);
  };

  const handleExportClick = async () => {
    if (isExporting || !conversationId) return;
    setIsExporting(true);
    try {
      await onExport(conversationId, "docx");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Stack horizontal className={style.ButtonsContainer}>
      <>
        {canViewMultipleDg && (
          <DgEnvironmentSelector
            currentDg={currentDg}
            onDgSwitch={onDgSwitch}
          />
        )}
        <MetadataDropdown
          onFolderStructureChanged={onFolderStructureChanged}
          isWorkflowSelected={useCaseTitle != DEFAULT_USE_CASE_NAME}
          currentDg={currentDg}
        />
        <HistoryButton
          onClick={onSetShowHistoryPanel}
          text={`${showHistoryPanel ? "Hide" : "Show"} Chat History`}
          className={style.HeaderButton}
        />

        {showExport && (
          <DefaultButton
            className={style.HeaderButton}
            onClick={handleExportClick}
            disabled={!conversationId || isExporting}
            onRenderIcon={
              isExporting
                ? () => <Spinner size={SpinnerSize.xSmall} />
                : () => <Download fontSize="small" />
            }
            text={isExporting ? "Exporting..." : "Export"}
          />
        )}
      </>
    </Stack>
  );
};

export default HeaderButtons;
